import { useContext, useEffect } from 'react'
import useSWR from 'swr'
import { crmCodes } from '@constants'

import { useModuleContext } from '../contexts/module'
import { getSiteLanguage } from '@utils'
import { PageContext } from '@providers/PageContext'
import useRequestLabels from '@hooks/useRequestLabels'
import useArtworkInformation from './useArtworkInformation'
import useAttachments from './useAttachments'
import useRoomsInformation from './useRoomsInformation'
import { useRouter } from 'next/router'
import useNextArtworkImagePreview from './useNextArtworkImagePreview'

/**
 * Module initializer
 * As artwork navigation is CSR the props changes, and we need to update the provider accordingly, thus the need of
 * props that are the props that come from the server and are the cms mapping and data
 * @returns {boolean}
 */
const useInitializer = () => {
  const router = useRouter()
  const { dispatch, state: { roomsInformation } } = useModuleContext()
  const { page: { pageData } } = useContext(PageContext)

  useRequestLabels([
    'OnlineShowRoom','ShowRoomArtwork', 'AltTags', 'ArtworkUploader',
    'ArtworkContactForm', 'Attachments', 'Chat'], useModuleContext)

  const { data: showInformation, error: showError } = useSWR(
    pageData?.eventDataAssociated?.onlineShowId
      ? `/msvc/v1/onlineshows/views/${pageData.eventDataAssociated.onlineShowId}?locale=${getSiteLanguage()}`
      : null
  )

  const { data: accountInformation, error: accountError } = useSWR(
    pageData?.accountCrmGuid ? `/msvc/v1/accounts/items/${pageData.accountCrmGuid}` : null
  )

  /**
   * By default we load the account data to see which type of account we are dealing with
   * If the account is type gallery, then we request the Gallery API
   */
  const { data: galleryInformation, error: galleryError } = useSWR(
    accountInformation && accountInformation.type === crmCodes.galleryAccount && pageData.galleryId
      ? `/msvc/v1/artcatalog/gallery/item/${pageData.galleryId}`
      : null
  )

  /**
   * Stores the online show data, gallery data and account data
   * Incase of when the show is ended we dont have some event info so we dont store show info
   * we will set the gallery data and account data in show close part as it ended in this bug MCHGB-9240
   */
  useEffect(() => {
    if (accountInformation || showInformation) {
      if (
        accountInformation?.type !== crmCodes.galleryAccount ||
        (accountInformation?.type === crmCodes.galleryAccount && galleryInformation)
      ) {
        dispatch({
          type: 'SET_INITIAL_STATE',
          payload: {
            onlineShowData: showInformation ? showInformation : null,
            galleryData: galleryInformation,
            accountData: accountInformation
          }
        })
      }
    }
  }, [accountInformation, dispatch, galleryInformation, showInformation])

  // Resets the Recommendations on every page navigation, related to I.Observer, etc.
  useEffect(() => {
    dispatch({
      type: 'TRIGGER_RECOMMENDATIONS',
      payload: false
    })
  }, [router, dispatch])

  useAttachments()
  useRoomsInformation()
  useArtworkInformation(pageData)
  /**
   * This Hook clones a part of the code in useArtworkInformation hook
   * that makes reference to "load image".
   * This is used to preload the coming image in background.
   */
  useNextArtworkImagePreview(roomsInformation?.nextArtwork)

  return showError || galleryError || accountError
}

export default useInitializer
